/**
 * SchedulingConflictsPanel.tsx
 * Collapsible banner showing hard and soft scheduling conflicts.
 * Phase 2, Item 7.
 *
 * Behaviour:
 *   - Renders below the toolbar when there are any conflicts.
 *   - Collapsed default: one-line summary with red/orange chip counts.
 *   - Expanded: full list of hard conflicts (grouped by machine) and soft
 *     conflicts, each with a "View" button that calls onJumpToBooking.
 *   - If no conflicts, renders nothing.
 *
 * Design: matches the "Scheduling Conflicts" warning area with "View Conflicts"
 * button shown in Machine Schedule.png mockup (bottom of the grid area).
 */

import React, { useState } from 'react';
import {
  Box,
  Collapse,
  Typography,
  Chip,
  IconButton,
  Button,
  Divider,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import type { ConflictSummary, HardConflict, SoftConflict } from '../../../types/schedule';

interface SchedulingConflictsPanelProps {
  conflicts: ConflictSummary;
  /**
   * Called when the user clicks "View" on a conflict entry.
   * The parent should scroll the grid to the specified booking and flash it.
   */
  onJumpToBooking: (bookingKey: string) => void;
}

// ── Hard conflict list item ─────────────────────────────────────────────────

interface HardConflictItemProps {
  conflict: HardConflict;
  onJump: (key: string) => void;
}

const HardConflictItem: React.FC<HardConflictItemProps> = ({ conflict, onJump }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        py: 0.75,
        px: 1.5,
        borderLeft: `3px solid ${theme.schedule.conflictHard}`,
        backgroundColor: alpha(theme.schedule.conflictHard, 0.04),
        borderRadius: '0 4px 4px 0',
        mb: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <ErrorOutlineIcon
          sx={{ fontSize: 14, color: theme.schedule.conflictHard, flexShrink: 0 }}
        />
        <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.72rem' }}>
          {conflict.machineCode} — {conflict.machineName}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.67rem' }}>
          {conflict.overlapStart} to {conflict.overlapEnd}
        </Typography>
      </Box>

      <Box sx={{ pl: 2.5, mt: 0.25 }}>
        {conflict.bookings.map((b) => (
          <Box
            key={b.key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mt: 0.25,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="caption" sx={{ fontSize: '0.67rem', flex: 1, minWidth: 80 }}>
              {b.taskName ?? b.source} ({b.startDate} — {b.endDate})
            </Typography>
            <Button
              size="small"
              variant="text"
              sx={{
                minWidth: 0,
                px: 0.75,
                py: 0,
                fontSize: '0.65rem',
                color: theme.schedule.conflictHard,
                lineHeight: 1.6,
              }}
              onClick={() => onJump(b.key)}
            >
              View
            </Button>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ── Soft conflict list item ──────────────────────────────────────────────────

interface SoftConflictItemProps {
  conflict: SoftConflict;
  onJump: (key: string) => void;
}

const SoftConflictItem: React.FC<SoftConflictItemProps> = ({ conflict, onJump }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.5,
        px: 1.5,
        borderLeft: `3px solid ${theme.schedule.conflictSoft}`,
        backgroundColor: alpha(theme.schedule.conflictSoft, 0.04),
        borderRadius: '0 4px 4px 0',
        mb: 0.5,
        flexWrap: 'wrap',
      }}
    >
      <WarningIcon sx={{ fontSize: 14, color: theme.schedule.conflictSoft, flexShrink: 0 }} />
      <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.72rem' }}>
        {conflict.machineCode}
      </Typography>
      <Typography variant="caption" sx={{ fontSize: '0.67rem', flex: 1, minWidth: 80 }}>
        {conflict.taskName ?? '(unknown task)'} — missing capability
      </Typography>
      <Button
        size="small"
        variant="text"
        sx={{
          minWidth: 0,
          px: 0.75,
          py: 0,
          fontSize: '0.65rem',
          color: theme.schedule.conflictSoft,
          lineHeight: 1.6,
        }}
        onClick={() => onJump(conflict.bookingKey)}
      >
        View
      </Button>
    </Box>
  );
};

// ── Main panel ───────────────────────────────────────────────────────────────

export const SchedulingConflictsPanel: React.FC<SchedulingConflictsPanelProps> = ({
  conflicts,
  onJumpToBooking,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  const hardCount = conflicts.hard.length;
  const softCount = conflicts.soft.length;
  const totalCount = hardCount + softCount;

  // Render nothing when there are no conflicts
  if (totalCount === 0) return null;

  const bannerColor =
    hardCount > 0
      ? theme.schedule.conflictHard
      : theme.schedule.conflictSoft;

  return (
    <Box
      sx={{
        border: `1px solid ${alpha(bannerColor, 0.4)}`,
        borderRadius: 1,
        mb: 1.5,
        overflow: 'hidden',
      }}
      data-testid="scheduling-conflicts-panel"
    >
      {/* ── Collapsed header bar ──────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          backgroundColor: alpha(bannerColor, 0.06),
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => setExpanded((prev) => !prev)}
        data-testid="conflicts-panel-header"
      >
        <WarningIcon sx={{ fontSize: 16, color: bannerColor, flexShrink: 0 }} />

        <Typography
          variant="caption"
          fontWeight={700}
          sx={{ fontSize: '0.75rem', color: bannerColor, flexShrink: 0 }}
        >
          Scheduling Conflicts
        </Typography>

        {hardCount > 0 && (
          <Chip
            label={`${hardCount} Hard`}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.62rem',
              fontWeight: 700,
              backgroundColor: theme.schedule.conflictHard,
              color: '#fff',
              ml: 0.5,
            }}
          />
        )}
        {softCount > 0 && (
          <Chip
            label={`${softCount} Soft`}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.62rem',
              fontWeight: 700,
              backgroundColor: theme.schedule.conflictSoft,
              color: '#fff',
              ml: 0.5,
            }}
          />
        )}

        <Box sx={{ flex: 1 }} />

        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
          {expanded ? 'Hide details' : 'View Conflicts'}
        </Typography>
        <IconButton size="small" sx={{ p: 0.25 }} aria-label={expanded ? 'collapse' : 'expand'}>
          {expanded ? (
            <ExpandLessIcon sx={{ fontSize: 16 }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Box>

      {/* ── Expanded conflict list ──────────────────────────────────────── */}
      <Collapse in={expanded}>
        <Box sx={{ px: 1.5, py: 1 }}>
          {/* Hard conflicts */}
          {hardCount > 0 && (
            <>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: 'block',
                  mb: 0.5,
                  color: theme.schedule.conflictHard,
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                Hard Conflicts — Overlapping Bookings
              </Typography>
              {conflicts.hard.map((c, idx) => (
                <HardConflictItem
                  key={`${c.machineKey}-${idx}`}
                  conflict={c}
                  onJump={onJumpToBooking}
                />
              ))}
            </>
          )}

          {/* Divider between sections */}
          {hardCount > 0 && softCount > 0 && (
            <Divider sx={{ my: 1 }} />
          )}

          {/* Soft conflicts */}
          {softCount > 0 && (
            <>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: 'block',
                  mb: 0.5,
                  color: theme.schedule.conflictSoft,
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                Soft Conflicts — Missing Capability
              </Typography>
              {conflicts.soft.map((c) => (
                <SoftConflictItem
                  key={c.bookingKey}
                  conflict={c}
                  onJump={onJumpToBooking}
                />
              ))}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
